import { useState } from 'react';
import { CheckCircle2, Trash2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  messageFromError,
  toast,
} from '@eo/shared';

import { ProviderConnectForm } from './ProviderConnectForm';
import type { ManageIntegrationDialogProps } from './types';

export function ManageIntegrationDialog({
  descriptor,
  status,
  onOpenChange,
  onSaveToken,
  onRemove,
}: ManageIntegrationDialogProps) {
  const [busy, setBusy] = useState(false);

  if (!descriptor) return null;

  const disconnectLabel = descriptor.kind === 'oauth' ? 'Disconnect' : `Remove ${descriptor.noun}`;

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onRemove(descriptor.key);
      toast.success(`${descriptor.title} disconnected.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(messageFromError(err, 'Disconnecting failed. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{descriptor.title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <span className="flex items-center gap-2 rounded-8 border border-border-faint px-3 py-2 text-sm text-text-primary">
            <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden="true" />
            <span className="truncate">Connected{status.detail ? ` · ${status.detail}` : ''}</span>
          </span>

          <ProviderConnectForm
            descriptor={descriptor}
            status={status}
            onSaveToken={(token) => onSaveToken(descriptor.key, token)}
            onDone={() => onOpenChange(false)}
          />

          <div className="border-t border-border-faint pt-4">
            <Button variant="destructive" onClick={() => void remove()} disabled={busy}>
              <Trash2 className="size-4" />
              {disconnectLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
